import React, { useState } from "react";
import "../App.css";
import {
  postWrapupCodes,
  getQueue,
  postWrapupCodesInQueues,
} from "../utilities/genesysCloudUtils";
import {
  AlertBlock,
  CodeFence,
  DxButton,
  DxCheckbox,
  DxItemGroup,
  DxTextbox,
} from "genesys-react-components";
import { GenesysDevIcons } from "genesys-dev-icons";
import Editor from "react-simple-code-editor";
import { highlight, languages } from "prismjs/components/prism-core";
import "prismjs/components/prism-clike";
import "prismjs/components/prism-javascript";
import "prismjs/components/prism-markup";
import "prismjs/themes/prism.css";

const hightlightWithLineNumbers = (input, language) =>
  highlight(input, language)
    .split("\n")
    .map((line, i) => `<span class='editorLineNumber'>${i + 1}</span>${line}`)
    .join("\n");

function setTextAreaFormat(value, maxLines, maxTextForLine) {
  let lines = [];
  if (value.length >= maxTextForLine) {
    lines = value.match(new RegExp(`.{1,${maxTextForLine}}`, "g"));
  } else {
    lines = value.split("\n");
  }
  if (lines.length > maxLines) {
    lines.length = maxLines;
  }
  return lines.join("\n");
}

export function CreateWrapupCode({ divisionsItemGroup }) {
  const [names, setNames] = useState([]);
  const [descriptions, setDescriptions] = useState([]);
  const [ids, setIds] = useState(null);
  const [error, setError] = useState("");
  const [queueResults, setQueueResults] = useState([]);
  const [selectedQueues, setSelectedQueues] = useState([]);
  const [selectedDivision, setSelectedDivision] = useState();
  const [confirmation, setConfirmation] = useState("");
  const [nameValue, setNameValue] = useState("");
  const [descriptionValue, setDescriptionValue] = useState("");

  const MAX_LINES = 100;
  const MAX_TEXT_FOR_LINE = 256;

  const handleNamesChange = (value) => {
    const lines = value.split("\n");
    if (lines.length > MAX_LINES) {
      setError("Error: Limit of 100 lines");
      return;
    }
    setError(""); // Clear the error if the input is valid
    const formattedValue = setTextAreaFormat(value, MAX_LINES, MAX_TEXT_FOR_LINE);
    setNameValue(formattedValue);
    setNames(formattedValue.split("\n"));
  };

  const handleDescriptionsChange = (value) => {
    const lines = value.split("\n");
    if (lines.length > MAX_LINES) {
      setError("Error: Limit of 100 lines");
      return;
    }
    setError(""); // Clear the error if the input is valid
    const formattedValue = setTextAreaFormat(value, MAX_LINES, MAX_TEXT_FOR_LINE);
    setDescriptionValue(formattedValue);
    setDescriptions(formattedValue.split("\n"));
  };

  const handleDivisionChange = (value) => {
    setSelectedDivision(value);
  };

  const createWrapupCodeObjects = () => {
    if (names.length !== descriptions.length) {
      setError("A wrapup code must have a description.");
      return;
    } else {
      for (let i = 0; i < descriptions.length; i++) {
        if (descriptions[i] === "") {
          setError("Each wrapup code must have a description");
          return;
        } else {
          setError("");
        }
      }
      const wrapupCodeObjects = names.map((name, index) => {
        const wrapupCode = {
          name: name,
          description: descriptions[index],
        };
        if (selectedDivision) {
          wrapupCode.division = { id: selectedDivision };
        }
        return wrapupCode;
      });
      console.log(wrapupCodeObjects);
      return wrapupCodeObjects;
    }
  };

  const createWrapupCodes = () => {
    const wrapupCodeObjects = createWrapupCodeObjects();
    if (!wrapupCodeObjects) return; // If there's an error, stop execution
    postWrapupCodes(wrapupCodeObjects)
      .then((data) => {
        setIds(data);
        setConfirmation("Wrapup codes created successfully.");
        setError("");
      })
      .catch((error) => {
        console.log(error);
        setError("Duplicate names are not allowed");
      });
    setTimeout(() => {
      setConfirmation("");
    }, 5000);
  };

  const handleQueueSearch = (value) => {
    if (value.trim() === "") {
      setQueueResults([]);
      return;
    }
    getQueue(value, selectedDivision).then((data) => {
      setQueueResults(data.entities);
    });
  };

  const handleQueueCheckChange = (queueId, checked) => {
    setSelectedQueues((prevSelectedQueues) => {
      if (checked) {
        return [...prevSelectedQueues, queueId];
      } else {
        return prevSelectedQueues.filter((id) => id !== queueId);
      }
    });
  };

  const handleAssign = () => {
    console.log(ids)
    postWrapupCodesInQueues(selectedQueues, ids)
      .then(() => {
        setConfirmation("Wrapup codes assigned successfully.");
      })
      .catch((error) => {
        console.log(error);
        setError("Error assigning wrapup codes.");
      });
    setTimeout(() => {
      setConfirmation("");
    }, 5000);
  };

  return (
    <>
      <div className="wrapupcode-form">
        <label className="dx-label">
          <span className="label-text">Wrapup Code Name</span>
          <Editor
            value={nameValue}
            onValueChange={(value) => handleNamesChange(value)}
            highlight={(code) =>
              hightlightWithLineNumbers(code, languages.markup)
            }
            padding={10}
            textareaId="nameArea"
            className="editor dx-textarea"
          />
          <div className="input-description">
            <i className="icon icon-app-info-solid "></i>
            <span>One per line. Limit of 100 lines.</span>
          </div>
        </label>

        <label className="dx-label">
          <span className="label-text">Wrapup Code Description</span>
          <Editor
            value={descriptionValue}
            onValueChange={(value) => handleDescriptionsChange(value)}
            highlight={(code) =>
              hightlightWithLineNumbers(code, languages.markup)
            }
            padding={10}
            textareaId="descriptionArea"
            className="editor dx-textarea"
          />
          <div className="input-description">
            <i className="icon icon-app-info-solid "></i>
            <span>
              Each wrapup code must have a description. Separate each
              description with a new line. Limit of 100 lines.
            </span>
          </div>
        </label>

        <DxItemGroup
          items={divisionsItemGroup}
          title="Division"
          format="dropdown"
          selectedItem={selectedDivision}
          onItemChanged={(item) => handleDivisionChange(item.value)}
          description="Optional. If no division is selected, the wrapup code will be created for all divisions."
        />
        <DxButton
          type="primary"
          onClick={() => createWrapupCodes()}
          disabled={false}
        >
          Submit
        </DxButton>
        {!error && ids ? (
          <>
            <DxTextbox
              inputType="text"
              label="Queue Search"
              icon={GenesysDevIcons.AppSearch}
              clearButton={true}
              onChange={(value) => handleQueueSearch(value)}
              changeDebounceMs={500}
              description="Search for Queues to assign the wrapup code to."
            />
            {queueResults.length > 0 &&
              queueResults.map((queue) => (
                <DxCheckbox
                  key={queue.id}
                  label={queue.name}
                  value="item-value"
                  initialValue={selectedQueues.includes(queue.id)}
                  name="name-string"
                  onCheckChanged={(checked) =>
                    handleQueueCheckChange(queue.id, checked)
                  }
                />
              ))}
            {selectedQueues.length > 0 && (
              <DxButton type="primary" onClick={handleAssign}>
                Assign
              </DxButton>
            )}
            {confirmation && (
              <AlertBlock
                title={confirmation}
                alertType="success"
                collapsible={false}
                autoCollapse={false}
                indentation={1}
                className="clazzy-alertblock"
              />
            )}
          </>
        ) : (
          error && (
            <AlertBlock
              title={error}
              alertType="warning"
              collapsible={false}
              autoCollapse={false}
              indentation={1}
              className="clazzy-alertblock"
            />
          )
        )}
      </div>
      <div className="submission-result">
        {ids && (
          <CodeFence
          autoCollapse={false}
          value={JSON.stringify(ids, null, 2)}
          language="json"
          jsonEditor={true}
          />
        )}
      </div>
      {/* <CreateCode /> */}
    </>
  );
}
